import type { UserOutput } from "@orsane/schema/users";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import avatarUrl from "@/lib/image-urls";
import { cn } from "@/lib/utils";
import { userFallbackColor } from "./UserAvatar";

interface StackedAvatarsProps {
    users: UserOutput[];
    /** Max number of avatars to show */
    max?: number;
    /** Size of avatars in pixels */
    size?: number;
    /** Overlap amount in pixels */
    overlap?: number;
    /** Show the remaining count */
    showCount?: boolean;
    /** Additional CSS classes */
    className?: string;
    /** Suffix to show in tooltip */
    tooltipSuffix?: string;
}

export function StackedAvatars({ users, max = 4, size = 40, overlap = 8, showCount = true, className, tooltipSuffix = "" }: StackedAvatarsProps) {
    const visibleUsers = users.slice(0, max);
    const remainingCount = Math.max(0, users.length - max);

    if (users.length === 0) {
        return null;
    }

    return (
        <div className={cn("flex items-center", className)} style={{ paddingRight: overlap }}>
            {visibleUsers.map((user, index) => (
                <Tooltip key={user.id}>
                    <TooltipTrigger
                        render={
                            <Avatar
                                className={cn("border border-neutral-50")}
                                style={{
                                    width: size,
                                    height: size,
                                    marginLeft: index > 0 ? -overlap : 0,
                                    zIndex: visibleUsers.length - index,
                                }}
                            >
                                <AvatarImage src={avatarUrl(user.image, "70")} alt={`${user.name}'s profile picture`} />
                                <AvatarFallback className="text-zinc-400 text-sm font-medium" style={{ backgroundColor: userFallbackColor(user) }}>
                                    <span className="text-white opacity-80">{user.name.charAt(0)}</span>
                                </AvatarFallback>
                            </Avatar>
                        }
                    ></TooltipTrigger>
                    <TooltipContent side="bottom" align="center">
                        {user.name} {tooltipSuffix}
                    </TooltipContent>
                </Tooltip>
            ))}

            {showCount && remainingCount > 0 && (
                <div
                    className="flex items-center justify-center rounded-full border-1 border-zinc-50  bg-zinc-100 text-zinc-500"
                    style={{
                        width: size,
                        height: size,
                        marginLeft: -overlap + 4,
                        fontSize: size * 0.4,
                        fontWeight: 500,
                    }}
                >
                    +{remainingCount}
                </div>
            )}
        </div>
    );
}
